import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookUser, IndianRupee, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtRs = (v) => `₹${Number(v || 0).toFixed(2)}`;

export default function Credits() {
  const [items, setItems] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/credits").then(({ data }) => setItems(data));
  useEffect(() => { load().catch(() => {}); }, []);

  const totalDue = items.reduce((s, i) => s + (i.due_amount || 0), 0);

  const collect = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter an amount");
    setSaving(true);
    try {
      const { data } = await api.post("/credits/pay", { invoice_id: payFor.id, amount: Number(amount), mode });
      toast.success(`Collected ${fmtRs(data.paid)}. Remaining: ${fmtRs(data.remaining_due)}`);
      setPayFor(null); setAmount("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="credits-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <BookUser className="w-6 h-6 text-emerald-700" /> Credit Ledger (Udhaar)
          </h1>
          <p className="text-sm text-slate-500">Pending balances for medicines sold on credit.</p>
        </div>
        <Card className="px-5 py-3 border-red-200 bg-red-50/50" data-testid="total-due-card">
          <div className="text-xs uppercase tracking-wider text-red-600 font-medium">Total Outstanding</div>
          <div className="font-mono-num text-2xl font-extrabold text-red-700">{fmtRs(totalDue)}</div>
        </Card>
      </div>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table data-testid="credits-table">
            <TableHeader><TableRow>
              <TableHead>Customer</TableHead><TableHead>Invoice</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">Bill</TableHead><TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((inv) => (
                <TableRow key={inv.id} data-testid={`credit-row-${inv.id}`}>
                  <TableCell>
                    <div className="font-semibold">{inv.customer_name}</div>
                    <div className="text-xs text-slate-500 font-mono-num">{inv.customer_phone}</div>
                  </TableCell>
                  <TableCell className="font-mono-num">{inv.invoice_no}</TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(inv.created_at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtRs(inv.grand_total)}</TableCell>
                  <TableCell className="text-right font-mono-num text-emerald-700">{fmtRs(inv.paid_amount)}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-red-700">{fmtRs(inv.due_amount)}</TableCell>
                  <TableCell><Badge className={inv.payment_status === "partial" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>{inv.payment_status === "partial" ? "Partial" : "Credit"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" data-testid={`collect-btn-${inv.id}`} onClick={() => { setPayFor(inv); setAmount(String(inv.due_amount)); }} className="bg-emerald-900 hover:bg-emerald-800 h-8">
                      <IndianRupee className="w-3.5 h-3.5 mr-1" /> Collect
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No outstanding credit — all clear!</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-sm bg-white" data-testid="collect-dialog">
          <DialogHeader><DialogTitle className="font-display">Collect Payment — {payFor?.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-red-50 border border-red-100 flex justify-between">
              <span className="text-sm text-red-700">Balance due</span>
              <span className="font-mono-num font-bold text-red-700">{fmtRs(payFor?.due_amount)}</span>
            </div>
            <div>
              <Label className="text-xs">Amount Received (₹)</Label>
              <Input data-testid="collect-amount" type="number" min="1" max={payFor?.due_amount} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-11 font-mono-num" />
            </div>
            <div>
              <Label className="text-xs">Payment Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="collect-mode" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Cancel</Button>
            <Button onClick={collect} disabled={saving} data-testid="collect-submit" className="bg-emerald-900 hover:bg-emerald-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Collect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
